'use client';

import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import {
  getConsent,
  saveConsent,
  createConsentRecord,
  type ConsentRecord,
} from '@/lib/consent';

interface ConsentPreferenceCenterProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;
}

export default function ConsentPreferenceCenter({
  isOpen,
  onClose,
  onSave,
}: ConsentPreferenceCenterProps) {
  const [choices, setChoices] = useState<ConsentRecord['choices']>({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) {
      const existing = getConsent();
      if (existing) {
        setChoices(existing.choices);
      }
      // Focus trap: focus first focusable element
      setTimeout(() => {
        firstFocusableRef.current?.focus();
      }, 0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleToggle = (category: keyof ConsentRecord['choices']) => {
    if (category === 'necessary') return; // Necessary cannot be toggled
    setChoices((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSave = () => {
    const consent = getConsent();
    const record = createConsentRecord(consent?.region || 'gdpr', choices);
    saveConsent(record);
    onSave?.();
    onClose();
  };

  const handleAcceptAll = () => {
    const consent = getConsent();
    const record = createConsentRecord(consent?.region || 'gdpr', {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    });
    saveConsent(record);
    onSave?.();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="preferences-title"
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-200">
            <h2 id="preferences-title" className="text-lg font-semibold text-slate-900">
              Cookie Preferences
            </h2>
            <button
              ref={closeButtonRef}
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-400 rounded"
              aria-label="Close preferences"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Necessary */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-slate-900">Necessary</label>
                <button
                  role="switch"
                  aria-checked
                  disabled
                  className="relative inline-flex h-6 w-11 items-center rounded-full bg-teal-600 cursor-not-allowed"
                >
                  <span className="inline-block h-4 w-4 transform rounded-full bg-white ml-1" />
                </button>
              </div>
              <p className="text-xs text-slate-500">
                Essential for site functionality, security, and authentication. Always enabled.
              </p>
            </div>

            {/* Analytics */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-slate-900">Analytics</label>
                <button
                  ref={firstFocusableRef}
                  role="switch"
                  aria-checked={choices.analytics}
                  onClick={() => handleToggle('analytics')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400 ${
                    choices.analytics ? 'bg-teal-600' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      choices.analytics ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <p className="text-xs text-slate-500">
                Help us understand how you use SafePlay to improve your experience.
              </p>
            </div>

            {/* Marketing */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-slate-900">Marketing</label>
                <button
                  role="switch"
                  aria-checked={choices.marketing}
                  onClick={() => handleToggle('marketing')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400 ${
                    choices.marketing ? 'bg-teal-600' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      choices.marketing ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <p className="text-xs text-slate-500">
                Allow us to personalize ads and content based on your interests.
              </p>
            </div>

            {/* Preferences */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-slate-900">Preferences</label>
                <button
                  role="switch"
                  aria-checked={choices.preferences}
                  onClick={() => handleToggle('preferences')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400 ${
                    choices.preferences ? 'bg-teal-600' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      choices.preferences ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <p className="text-xs text-slate-500">
                Remember your preferences for a personalized experience.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-6 border-t border-slate-200 bg-slate-50">
            <button
              onClick={handleSave}
              className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              Save Preferences
            </button>
            <button
              onClick={handleAcceptAll}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
